import React, { useCallback, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import COLORS from "../resources/colors/COLORS";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatMonthLabel(item) {
  const raw = item?.applicable_month || item?.service_month || "";
  const m = String(raw).match(/^(\d{4})-(\d{1,2})/);
  if (!m) return raw || "—";
  const monthIndex = parseInt(m[2], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return raw;
  return `${MONTH_NAMES[monthIndex]} ${m[1]}`;
}

const MdlEditServiceMonth = ({ item, actionClose, actionSave }) => {
  const originalItem = item || {};
  const [show, setShow] = useState(true);
  const [netServiceFee, setNetServiceFee] = useState(
    originalItem.net_service_fee ?? ""
  );

  const monthLabel = useMemo(
    () => formatMonthLabel(originalItem),
    [originalItem]
  );

  const handleClose = useCallback(() => {
    setShow(false);
    if (typeof actionClose === "function") actionClose();
  }, [actionClose]);

  const handleSave = useCallback(() => {
    const fee = String(netServiceFee ?? "").trim();
    if (fee === "" || Number.isNaN(Number(fee))) {
      return;
    }

    const updatedData = {
      ...originalItem,
      net_service_fee: fee,
      net_service_fee_old: originalItem.net_service_fee,
      service_fee: originalItem.service_fee || originalItem.net_service_fee,
      service_month:
        originalItem.service_month || originalItem.applicable_month,
    };

    if (typeof actionSave === "function") actionSave(updatedData);
    handleClose();
  }, [netServiceFee, originalItem, actionSave, handleClose]);

  const canSave =
    String(netServiceFee ?? "").trim() !== "" &&
    !Number.isNaN(Number(netServiceFee));

  return (
    <>
      <style>{`
        .mdl-etsm .modal-content {
          border-radius: 14px !important;
          border: 1.5px solid #81d4fa !important;
          overflow: hidden;
        }
        .mdl-etsm .modal-header .close {
          outline: none;
        }
        .mdl-etsm .mdl-etsm__actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        /* Native buttons — avoid global .btn { background-image: none !important } */
        .mdl-etsm .mdl-etsm__cancel,
        .mdl-etsm .mdl-etsm__save {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 36px;
          padding: 6px 16px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 750;
          cursor: pointer;
          margin: 0;
          -webkit-appearance: none;
          appearance: none;
        }
        .mdl-etsm .mdl-etsm__cancel {
          color: ${COLORS.primary_color_shade9} !important;
          background-color: #ffffff !important;
          background-image: none !important;
          border: 1.5px solid ${COLORS.primary_color_shade2} !important;
        }
        .mdl-etsm .mdl-etsm__cancel:hover {
          background-color: ${COLORS.primary_color_shade0} !important;
          color: ${COLORS.primary_color_shade9} !important;
        }
        .mdl-etsm .mdl-etsm__save {
          color: #ffffff !important;
          background-color: ${COLORS.primary_color_shade5} !important;
          background-image: linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%) !important;
          border: 1.5px solid ${COLORS.primary_color_shade7} !important;
          box-shadow: 0 4px 12px rgba(3, 169, 244, 0.28) !important;
        }
        .mdl-etsm .mdl-etsm__save:hover:not(:disabled) {
          color: #ffffff !important;
          background-color: ${COLORS.primary_color_shade6} !important;
          background-image: linear-gradient(135deg, ${COLORS.primary_color_shade4} 0%, ${COLORS.primary_color_shade7} 100%) !important;
        }
        .mdl-etsm .mdl-etsm__save:disabled {
          color: #ffffff !important;
          background-color: ${COLORS.primary_color_shade3} !important;
          background-image: none !important;
          border-color: ${COLORS.primary_color_shade3} !important;
          opacity: 0.9;
          cursor: not-allowed;
          box-shadow: none !important;
        }
        .mdl-etsm .mdl-etsm__input {
          font-size: 14px !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          min-height: 40px;
          padding: 8px 12px !important;
          border-radius: 10px !important;
          border: 1.5px solid ${COLORS.primary_color_shade2} !important;
          background: #fff !important;
        }
        .mdl-etsm .mdl-etsm__input:focus {
          border-color: ${COLORS.primary_color_shade5} !important;
          box-shadow: 0 0 0 3px rgba(3, 169, 244, 0.16) !important;
          outline: none;
        }
      `}</style>
      <Modal
        show={show}
        onHide={handleClose}
        animation={false}
        centered
        dialogClassName="mdl-etsm"
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: `1.5px solid ${COLORS.primary_color_shade1}`,
            padding: "12px 16px",
            background: `linear-gradient(180deg, ${COLORS.primary_color_shade0} 0%, #fff 100%)`,
          }}
        >
          <div style={{ width: "100%", paddingRight: 12 }}>
            <h5
              style={{
                margin: 0,
                fontWeight: 800,
                fontSize: "15px",
                color: COLORS.primary_color_shade9,
              }}
            >
              Edit Transport Service Fee
            </h5>
            <p
              style={{
                margin: "3px 0 0 0",
                fontSize: "12px",
                color: "#334155",
                fontWeight: 600,
              }}
            >
              Month: <strong>{monthLabel}</strong>
            </p>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: "12px 16px 8px" }}>
          <Form>
            <Form.Group>
              <Form.Label
                style={{
                  fontSize: "12px",
                  fontWeight: 800,
                  color: COLORS.primary_color_shade8,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  marginBottom: 6,
                }}
              >
                Net Service Fee (₹)
              </Form.Label>
              <Form.Control
                className="mdl-etsm__input"
                type="number"
                min="0"
                step="0.01"
                value={netServiceFee}
                onChange={(e) => setNetServiceFee(e.target.value)}
                placeholder="Enter net service fee"
              />
              <div
                style={{
                  marginTop: 6,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#334155",
                  lineHeight: 1.4,
                }}
              >
                Does not change collected or settled amounts — only the approved
                fee for this month.
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: `1.5px solid ${COLORS.primary_color_shade1}`,
            padding: "10px 16px 12px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            background: COLORS.primary_color_shade0,
          }}
        >
          <div className="mdl-etsm__actions">
            <button
              type="button"
              className="mdl-etsm__cancel"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mdl-etsm__save"
              onClick={handleSave}
              disabled={!canSave}
            >
              Save changes
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MdlEditServiceMonth;
